import { Globe } from 'lucide-react';
import { useTranslation } from '@sshop/i18n';

import { cn } from '@sshop/ui';

import { languageSwitcherStyles as s } from './LanguageSwitcher.styles';
import type { LanguageSwitcherProps } from './LanguageSwitcher.types';
import { useSwitchLanguage } from './useSwitchLanguage';

/**
 * Animated single-language toggle. Shows only the current language; click swaps
 * to the other and navigates while preserving path/search/hash.
 */
export function LanguageSwitcher({ className }: LanguageSwitcherProps) {
  const { t } = useTranslation();
  const { current, next, switchTo } = useSwitchLanguage();
  const label = (locale: typeof current) =>
    t(locale === 'ge' ? 'language.shortGe' : 'language.shortEn');
  const nextLanguage = t(next === 'ge' ? 'language.georgian' : 'language.english');

  return (
    <button
      type="button"
      className={cn(s.root, className)}
      onClick={switchTo}
      aria-label={t('language.switchTo', { language: nextLanguage })}
      title={label(next)}
    >
      <Globe className={s.icon} aria-hidden />
      <span className={s.labelWrap}>
        <span key={current} className={s.label}>
          {label(current)}
        </span>
      </span>
    </button>
  );
}
